import { useState, useEffect } from "react";
import PlatformBrand from "./PlatformBrand";
import { Link, useLocation } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { ScrollArea } from "./ui/scroll-area";
import { Button } from "./ui/button";
import { List, SignOut, X } from "@phosphor-icons/react";
import ThemeMenu from "./ThemeMenu";
import { haptic } from "../lib/haptics";
import NavTree from "./nav/NavTree";

/** Mobile burger drawer that exposes the FULL sidebar — same NAV_SECTIONS tree as desktop (collapsible groups
 * included), just slid-in from the left. Mounted only on mobile (md:hidden).
 *
 * Props:
 *   sections       — NAV_SECTIONS from nav/navSections.js
 *   user           — current user (for role filtering)
 *   counts         — { unreadCount, vendorMsgUnread, expensesPending, posDrafts }
 *   collapsible    — tenant preference: collapsible groups vs always expanded
 *   isMainClient / sectionAllowed — same visibility rules as the desktop sidebar
 *   onLogout
 */
export default function MobileNav({ sections, user, counts = {}, collapsible = true, isMainClient = false, sectionAllowed, onLogout }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Listen for the swipe-from-left-edge gesture (dispatched by useSwipeBack
  // when there's no "back" target). Provides a native-feeling drawer pull
  // on root pages like the dashboard.
  useEffect(() => {
    const onOpenRequested = () => {
      haptic("light");
      setOpen(true);
    };
    window.addEventListener("fixli:open-mobile-nav", onOpenRequested);
    return () => window.removeEventListener("fixli:open-mobile-nav", onOpenRequested);
  }, []);

  const close = () => setOpen(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          className="md:hidden -ml-2 p-2 rounded-md hover:bg-muted active:scale-95 transition-transform"
          aria-label="Open menu"
          data-testid="mobile-burger-btn"
        >
          <List size={22} />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[280px] p-0 flex flex-col" data-testid="mobile-burger-drawer">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 px-4 h-14 border-b border-border shrink-0">
          <Link to="/" onClick={close} className="flex items-center gap-2 min-w-0">
            <PlatformBrand variant="sidebar" />
          </Link>
          <button onClick={close} className="p-1.5 rounded-md hover:bg-muted" aria-label="Close menu" data-testid="mobile-burger-close">
            <X size={18} />
          </button>
        </div>

        {/* User chip */}
        {user && (
          <div className="px-4 py-3 border-b border-border bg-muted/40">
            <p className="text-sm font-semibold truncate">{user.name || "You"}</p>
            <p className="text-[11px] text-muted-foreground truncate">{(user.role || "").replace(/_/g, " ")}{user.email ? ` · ${user.email}` : ""}</p>
          </div>
        )}

        {/* Nav tree */}
        <ScrollArea className="flex-1">
          <nav className="flex flex-col gap-0.5 p-2">
            <NavTree sections={sections} user={user} location={location} collapsible={collapsible} counts={counts} isMainClient={isMainClient}
              sectionAllowed={sectionAllowed} onNavigate={close} testidPrefix="mobile-nav" />
          </nav>
        </ScrollArea>

        {/* Footer actions */}
        <div className="border-t border-border p-2 shrink-0 space-y-1">
          <ThemeMenu testid="mobile-theme-toggle" className="text-foreground" />
          <Button variant="ghost" size="sm" className="w-full justify-start gap-3 text-destructive hover:text-destructive" onClick={() => { close(); onLogout?.(); }} data-testid="mobile-logout">
            <SignOut size={18} />
            <span className="text-xs">Logout</span>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
